import { ChartSvg, ChartTitle } from "@/components/tool-kit/charts/chart-svg";
import { px } from "@/components/tool-kit/charts/marks";
import { textColorOn } from "@/lib/tool-kit/charts/color";
import { tint } from "@/lib/tool-kit/diagrams/color";
import {
  type BandLayout,
  CARD_PADDING,
  TIMELINE_FONT,
  type TimelineLayout,
} from "@/lib/tool-kit/diagrams/timeline";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * A timeline from `layoutTimeline`: the axis, its date ticks, period bands beside it,
 * and one card per event joined to its dot by a leader line. An approximate ("c.")
 * date gets a hollow dot. Band fills are pale tints, so a band's name in dark ink stays
 * readable and the bands never hide the axis.
 */

function Band({
  band,
  color,
  vertical,
}: {
  band: BandLayout;
  color: string;
  vertical: boolean;
}) {
  const fill = tint(color, 0.7);
  const cx = band.rect.x + band.rect.width / 2;
  const cy = band.rect.y + band.rect.height / 2;
  return (
    <g>
      <rect
        fill={fill}
        height={px(band.rect.height)}
        rx={3}
        stroke={color}
        strokeWidth={1}
        width={px(band.rect.width)}
        x={px(band.rect.x)}
        y={px(band.rect.y)}
      />
      {band.label ? (
        <text
          fill={textColorOn(fill)}
          fontSize={TIMELINE_FONT.band}
          textAnchor="middle"
          transform={vertical ? `rotate(-90 ${px(cx)} ${px(cy)})` : undefined}
          x={px(cx)}
          y={px(cy + TIMELINE_FONT.band * 0.35)}
        >
          {band.label}
        </text>
      ) : null}
    </g>
  );
}

function Ticks({ layout }: { layout: TimelineLayout }) {
  const vertical = layout.orientation === "vertical";
  return (
    <g>
      {layout.ticks.map((tick) => (
        <g key={`${tick.x}-${tick.y}`}>
          <line
            stroke={CHART_INK.axis}
            strokeWidth={1}
            x1={px(tick.x)}
            x2={px(vertical ? tick.x - 5 : tick.x)}
            y1={px(tick.y)}
            y2={px(vertical ? tick.y : tick.y + 5)}
          />
          <text
            fill={CHART_INK.muted}
            fontSize={TIMELINE_FONT.tick}
            textAnchor={vertical ? "end" : "middle"}
            x={px(vertical ? tick.x - 8 : tick.x)}
            y={px(vertical ? tick.y + TIMELINE_FONT.tick * 0.35 : tick.y + 19)}
          >
            {tick.label}
          </text>
        </g>
      ))}
    </g>
  );
}

export function TimelineDiagram({
  id,
  layout,
  accent,
  bandColors,
  label,
  description,
}: {
  id: string;
  layout: TimelineLayout;
  /** The colour of the event dots and the card edges. */
  accent: string;
  /** One colour per period. */
  bandColors: readonly string[];
  label: string;
  description: string;
}) {
  const { axis } = layout;
  const vertical = layout.orientation === "vertical";
  return (
    <ChartSvg
      description={description}
      height={layout.height}
      id={id}
      label={label}
      width={layout.width}
    >
      <ChartTitle title={layout.title} />
      {layout.bands.map((band) => (
        <Band
          band={band}
          color={bandColors[band.index] ?? CHART_INK.axis}
          key={`band-${band.index}`}
          vertical={vertical}
        />
      ))}
      <line
        stroke={CHART_INK.text}
        strokeLinecap="round"
        strokeWidth={2}
        x1={px(axis.x1)}
        x2={px(axis.x2)}
        y1={px(axis.y1)}
        y2={px(axis.y2)}
      />
      <Ticks layout={layout} />
      {layout.cards.map((card) => (
        <polyline
          fill="none"
          key={`leader-${card.index}`}
          points={card.leader.map(([x, y]) => `${px(x)},${px(y)}`).join(" ")}
          stroke={CHART_INK.axis}
          strokeWidth={1}
        />
      ))}
      {layout.cards.map((card) => (
        <g key={`card-${card.index}`}>
          <rect
            fill={CHART_INK.background}
            height={px(card.rect.height)}
            rx={4}
            stroke={tint(accent, 0.45)}
            strokeWidth={1}
            width={px(card.rect.width)}
            x={px(card.rect.x)}
            y={px(card.rect.y)}
          />
          {card.lines.map((line) => (
            <text
              fill={line.muted ? CHART_INK.muted : CHART_INK.text}
              fontSize={line.fontSize}
              fontWeight={line.bold ? "bold" : undefined}
              key={`${line.y}-${line.text}`}
              x={px(card.rect.x + CARD_PADDING)}
              y={px(card.rect.y + line.y)}
            >
              {line.text}
            </text>
          ))}
          <circle
            cx={px(card.dot.x)}
            cy={px(card.dot.y)}
            fill={card.circa ? CHART_INK.background : accent}
            r={5}
            stroke={accent}
            strokeWidth={card.circa ? 2 : 1}
          />
        </g>
      ))}
    </ChartSvg>
  );
}
